import { createContext, useContext, useMemo, type ReactNode } from "react";
import { Lexer, type Token, type Tokens } from "marked";
import { cx } from "@/components/ui";
import { marked, MENTION, SAFE_URL } from "@/lib/markdown";

/**
 * Renders the GitHub-flavoured Markdown Mixla stores in descriptions, steps, notes and comments,
 * parsed by the same marked setup as the editor (`@/lib/markdown`). Builds React elements from
 * marked's tokens, never HTML strings: raw HTML shows as text, images as links, and links must be
 * http(s), mailto or in-app paths. `inline` renders inline markup and line breaks only, in a <span>.
 */
export function RichText({ text, className, inline = false }: { text: string; className?: string; inline?: boolean }) {
  const nodes = useMemo(() => {
    const src = text.replace(/\r\n?/g, "\n");
    return inline ? inlines(Lexer.lexInline(src, marked.defaults), "i") : blocks(marked.lexer(src), "b");
  }, [text, inline]);
  return inline ? <span className={cx("rich", className)}>{nodes}</span> : <div className={cx("rich", className)}>{nodes}</div>;
}

// ───────────── Blocks ─────────────

function blocks(tokens: Token[], prefix: string): ReactNode[] {
  return tokens.map((tok, i) => block(tok, `${prefix}${i}`));
}

function block(tok: Token, k: string): ReactNode {
  const t = tok as Tokens.Generic;
  switch (tok.type) {
    case "space":
    case "def":
      return null;
    case "paragraph":
      return <p key={k}>{inlines((tok as Tokens.Paragraph).tokens, k)}</p>;
    case "heading": {
      const h = tok as Tokens.Heading;
      const H = `h${Math.min(6, Math.max(1, h.depth))}` as "h1";
      return <H key={k}>{inlines(h.tokens, k)}</H>;
    }
    case "code":
      return (
        <pre key={k}>
          <code>{(tok as Tokens.Code).text}</code>
        </pre>
      );
    case "blockquote":
      return <blockquote key={k}>{blocks((tok as Tokens.Blockquote).tokens, `${k}q`)}</blockquote>;
    case "hr":
      return <hr key={k} />;
    case "list":
      return <List key={k} list={tok as Tokens.List} k={k} />;
    case "table":
      return <Table key={k} table={tok as Tokens.Table} k={k} />;
    case "html":
      return <p key={k}>{(tok as Tokens.HTML).text}</p>;
    case "text": {
      // Loose block text (e.g. inside a tight list item).
      const x = tok as Tokens.Text;
      return x.tokens ? <span key={k}>{inlines(x.tokens, k)}</span> : <span key={k}>{decode(x.text)}</span>;
    }
    default:
      return t.tokens ? <div key={k}>{blocks(t.tokens, k)}</div> : t.raw ? <p key={k}>{t.raw}</p> : null;
  }
}

function List({ list, k }: { list: Tokens.List; k: string }) {
  const tasks = list.items.some((it) => it.task);
  const items = list.items.map((it, n) => (
    <li key={n} className={it.task ? "task" : undefined}>
      {it.task && <input type="checkbox" checked={!!it.checked} disabled readOnly aria-label={it.checked ? "done" : "to do"} />}
      {blocks(it.tokens.filter((x) => x.type !== "checkbox"), `${k}-${n}-`)}
    </li>
  ));
  const cls = tasks ? "tasks" : undefined;
  return list.ordered ? (
    <ol className={cls} start={list.start === "" || list.start === 1 ? undefined : Number(list.start)}>
      {items}
    </ol>
  ) : (
    <ul className={cls}>{items}</ul>
  );
}

function Table({ table, k }: { table: Tokens.Table; k: string }) {
  const align = (a: Tokens.TableCell["align"]) => (a ? { textAlign: a } : undefined);
  return (
    <div className="overflow-x-auto">
      <table>
        <thead>
          <tr>
            {table.header.map((c, i) => (
              <th key={i} style={align(c.align)}>
                {inlines(c.tokens, `${k}h${i}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, r) => (
            <tr key={r}>
              {row.map((c, i) => (
                <td key={i} style={align(c.align)}>
                  {inlines(c.tokens, `${k}r${r}c${i}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ───────────── Inline ─────────────

function inlines(tokens: Token[] | undefined, prefix: string): ReactNode[] {
  return (tokens ?? []).map((tok, i) => inline(tok, `${prefix}.${i}`));
}

const external = (href: string) => (href.startsWith("/") ? {} : { target: "_blank", rel: "noopener noreferrer" });

function link(href: string, label: ReactNode, k: string, title?: string | null) {
  return (
    <a key={k} href={href} title={title ?? undefined} {...external(href)}>
      {label}
    </a>
  );
}

function inline(tok: Token, k: string): ReactNode {
  switch (tok.type) {
    case "text": {
      const x = tok as Tokens.Text;
      return x.tokens ? <span key={k}>{inlines(x.tokens, k)}</span> : <span key={k}>{withMentions(decode(x.text), k)}</span>;
    }
    case "escape":
      return (tok as Tokens.Escape).text;
    case "strong":
      return <strong key={k}>{inlines((tok as Tokens.Strong).tokens, k)}</strong>;
    case "em":
      return <em key={k}>{inlines((tok as Tokens.Em).tokens, k)}</em>;
    case "del":
      return <del key={k}>{inlines((tok as Tokens.Del).tokens, k)}</del>;
    case "codespan":
      return <code key={k}>{decode((tok as Tokens.Codespan).text)}</code>;
    case "br":
      return <br key={k} />;
    case "link": {
      const l = tok as Tokens.Link;
      return SAFE_URL.test(l.href) ? link(l.href, inlines(l.tokens, k), k, l.title) : <span key={k}>{l.raw}</span>;
    }
    case "image": {
      // Never load images from a body: show the link instead.
      const im = tok as Tokens.Image;
      return SAFE_URL.test(im.href) ? link(im.href, im.text || im.href, k, im.title) : <span key={k}>{im.raw}</span>;
    }
    case "html":
      return <span key={k}>{(tok as Tokens.HTML).text}</span>;
    default: {
      const t = tok as Tokens.Generic;
      return t.tokens ? <span key={k}>{inlines(t.tokens, k)}</span> : <span key={k}>{t.raw}</span>;
    }
  }
}

/** marked leaves HTML entities in text (`&amp;`, `&#39;`) for an HTML renderer; React needs characters. */
function decode(s: string): string {
  if (!s.includes("&")) return s;
  const el = document.createElement("textarea");
  el.innerHTML = s;
  return el.value;
}

function withMentions(text: string, k: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = new RegExp(MENTION.source, "g");
  let last = 0;
  for (const m of text.matchAll(re)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<Mention key={`${k}@${m.index}`} username={(m[1] ?? "").toLowerCase()} />);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// ───────────── Mentions ─────────────

/** username → display name, for `@username` in rendered text. */
export const MentionNames = createContext<Record<string, string>>({});

function Mention({ username }: { username: string }) {
  const names = useContext(MentionNames);
  const name = names[username];
  return (
    <span title={`@${username}`} className={name ? "rounded bg-accent-soft px-1 font-medium text-accent" : "text-text-2"}>
      @{name ?? username}
    </span>
  );
}
